import { addDaysYmd, endOfLocalDayIso, parseDateOnly, todayYmd } from './date-only';

// WHEN A GRANT OF ACCESS ENDS. Buyer access (a buyer, or a member of a buyer's
// team) always ends on a date chosen when it is given — "ongoing" is never offered
// for it, let alone preselected (owner rule 2026-10-21). The seller's side and the
// intermediary's own team may still pick "Ongoing". Buyer access also defaults to
// 2 weeks and runs at most 2 months (owner, 2026-10-22). The server enforces the
// same rules; this is the one picker every admin screen uses.

export type AccessEndChoice = '1w' | '2w' | '1m' | '2m' | 'date' | 'ongoing';

export interface AccessEndValue {
  choice: AccessEndChoice;
  /** The picked calendar date ('YYYY-MM-DD'); used when `choice` is 'date'. */
  date: string;
}

/** The quick choices, in the order offered. */
export const ACCESS_END_QUICK_CHOICES = [
  { id: '1w', label: '1 week' },
  { id: '2w', label: '2 weeks' },
  { id: '1m', label: '1 month' },
  { id: '2m', label: '2 months' },
] as const satisfies readonly { id: AccessEndChoice; label: string }[];

/** What is selected when the picker opens for a buyer: two weeks. */
export const DEFAULT_ACCESS_END_CHOICE: AccessEndChoice = '2w';

/** The longest buyer access: this many calendar months from today (server-enforced too). */
export const MAX_BUYER_ACCESS_MONTHS = 2;

export function defaultAccessEnd(): AccessEndValue {
  return { choice: DEFAULT_ACCESS_END_CHOICE, date: '' };
}

/** `ymd` plus `months` calendar months; a day past the month's end clamps to its last day. */
export function addMonthsYmd(ymd: string, months: number): string {
  const d = parseDateOnly(ymd);
  if (!d) throw new Error(`Not a calendar date: ${ymd}`);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return todayYmd(d);
}

/** The last day a buyer's access can run through: today + 2 months. */
export function maxBuyerAccessEndYmd(today: string = todayYmd()): string {
  return addMonthsYmd(today, MAX_BUYER_ACCESS_MONTHS);
}

/**
 * The calendar date access runs through, or null for ongoing / no usable date yet.
 * `capped` (the default) is the buyer-side rule: a picked date later than
 * {@link maxBuyerAccessEndYmd} is not usable. The seller's and intermediary's own
 * people (`capped = false`) have no maximum.
 */
export function accessEndYmd(
  value: AccessEndValue,
  today: string = todayYmd(),
  capped = true,
): string | null {
  switch (value.choice) {
    case '1w':
      return addDaysYmd(today, 7);
    case '2w':
      return addDaysYmd(today, 14);
    case '1m':
      return addMonthsYmd(today, 1);
    case '2m':
      return addMonthsYmd(today, 2);
    case 'date': {
      const picked = parseDateOnly(value.date);
      // A past day is not an end date: access would already be over.
      if (!picked || value.date < today) return null;
      return capped && value.date > maxBuyerAccessEndYmd(today) ? null : value.date;
    }
    default:
      return null;
  }
}

/** The ISO instant to send as `expiresAt` (end of that day, viewer's time zone), or null. */
export function accessEndIso(
  value: AccessEndValue,
  today: string = todayYmd(),
  capped = true,
): string | null {
  const ymd = accessEndYmd(value, today, capped);
  return ymd ? endOfLocalDayIso(ymd) : null;
}

/** True when the value can be submitted: ongoing, or a real end date. */
export function accessEndReady(
  value: AccessEndValue,
  today: string = todayYmd(),
  capped = true,
): boolean {
  return value.choice === 'ongoing' || accessEndYmd(value, today, capped) !== null;
}
